import useSafeState from "../utils/safe-state";
import {Button, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin} from "antd";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const DdsAssignmentRuleDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);

    useEffect(() => {
        form.resetFields();
        getItems();

        if(props.mode === 'Edit') {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if(key === 'validFrom' || key === 'validTo') {
                    fieldsValues.push({name: key, value: value ? moment(value as string) : null});
                } else if(key === 'itemNo') {
                    fieldsValues.push({name: key, value: value});
                } else {
                    fieldsValues.push({name: key, value: value});
                }
            }
            form.setFields(fieldsValues);
        }
    }, []);

    const getItems = () => {
        if (axiosInstance.current != null) {
            setItemLoading(true);
            axiosInstance.current
                .get('/resource/items')
                .then(response => {
                    const items = Array.isArray(response.data) ? response.data : [];
                    setItemOptions(items);
                })
                .catch(function (error: any) {
                    console.log(error);
                })
                .finally(() => {
                    setItemLoading(false);
                });
        }
    };

    const handleEdit = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            axiosInstance.current
                .put(
                    '/resource/dds-assignment-rules/' + encodeURIComponent(form.getFieldValue(['id'])) + '/from-dto',
                    {
                        id: form.getFieldValue(['id']),
                        item: {
                            itemNo: form.getFieldValue(['itemNo'])
                        },
                        ddsNumber: form.getFieldValue(['ddsNumber']),
                        quantity: form.getFieldValue(['quantity']),
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null,
                        version: form.getFieldValue(['version'])
                    },
                    config,
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully updated.');
                    onReset();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const onReset = () => {
        form.resetFields();
    };

    const handleFinish = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            axiosInstance.current
                .post(
                    '/resource/dds-assignment-rules/from-dto',
                    {
                        item: {
                            itemNo: form.getFieldValue(['itemNo'])
                        },
                        ddsNumber: form.getFieldValue(['ddsNumber']),
                        quantity: form.getFieldValue(['quantity']),
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null
                    },
                    config,
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully created.');
                    onReset();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    return (
        <Form
            id="newDdsAssignmentRule"
            layout='vertical'
            form={form}
            onFinish={() => (props.mode === 'Edit' ? handleEdit() : handleFinish())}
        >
            <Spin spinning={loading || itemLoading}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="itemNo"
                            label={<Trans>Item No</Trans>}
                            rules={[{ required: true, message: <Trans>Please select the item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.itemNo} value={item.itemNo}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ddsNumber"
                            label={<Trans>DDS Number</Trans>}
                            rules={[{ required: true, message: <Trans>Please enter the DDS number</Trans> }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="quantity"
                            label={<Trans>Quantity</Trans>}
                        >
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="validFrom"
                            label={<Trans>Valid From</Trans>}
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="validTo"
                            label={<Trans>Valid To</Trans>}
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div>
                <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                    <Trans>Clear</Trans>
                </Button>
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default DdsAssignmentRuleDrawer;
